import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Pagination } from '@/components/common/Pagination'
import { NotificationItem } from '@/components/Notifications/NotificationItem'
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllRead,
} from '@/hooks/useNotifications'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { toast } from '@/stores/useToastStore'
import { cn } from '@/utils/cn'
import type {
  NotificationRead,
  NotificationCategory,
} from '@/types/notification'

const categories: { label: string; value: NotificationCategory | 'ALL' }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Chat', value: 'CHAT' },
  { label: 'Workflow', value: 'WORKFLOW' },
  { label: 'Approval', value: 'APPROVAL' },
  { label: 'Sourcing', value: 'SOURCING' },
  { label: 'Milestone', value: 'MILESTONE' },
]

export default function Notifications() {
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState<
    NotificationCategory | 'ALL'
  >('ALL')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllRead()
  const unreadCount = useNotificationStore(s => s.unreadCount)

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [activeCategory, pageSize])

  const offset = (page - 1) * pageSize
  const params = {
    offset,
    limit: pageSize,
    ...(activeCategory !== 'ALL' && { category: activeCategory }),
  }

  const { data, isLoading, isError } = useNotifications(params)

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load notifications')
    }
  }, [isError])

  const notifications = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const handleNotificationClick = (notification: NotificationRead) => {
    if (!notification.is_read) {
      markRead.mutate({ id: notification.id, is_read: true })
    }
    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  const handleActionClick = (
    notification: NotificationRead,
    url: string,
    state?: Record<string, unknown>
  ) => {
    if (!notification.is_read) {
      markRead.mutate({ id: notification.id, is_read: true })
    }
    navigate(url, { state })
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Notifications
          </h1>
          <p className="mt-1 text-base text-muted-foreground">
            Stay updated on your plans, approvals, and team activity
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={unreadCount === 0}
          onClick={() => markAllRead.mutate()}
        >
          <CheckCheck className="mr-2 h-4 w-4" />
          Mark all as read
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-1">
        {categories.map(cat => (
          <button
            key={cat.value}
            type="button"
            onClick={() => setActiveCategory(cat.value)}
            className={cn(
              'cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              activeCategory === cat.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Notification List */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <p className="text-sm">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <CheckCheck className="mb-2 h-10 w-10" />
            <p className="text-sm font-medium">You're all caught up!</p>
            <p className="mt-1 text-xs">No notifications to show</p>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
                onActionClick={handleActionClick}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </Card>
    </div>
  )
}
